import React, { useEffect, useState, useRef, useCallback } from "react";
import { adminApi, API, downloadBlob } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Download, Upload, X, Search, Eye, Mail, Send } from "lucide-react";
import { eur } from "../../lib/format";

const EMPTY = { name: "", company: "", cif: "", email: "", phone: "", address: "", postal_code: "", city: "", province: "", country: "España" };

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => adminApi.get("/admin/customers", { params: { search } }).then((r) => setCustomers(r.data)), [search]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImportFile(f);
    setImportPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await adminApi.post("/admin/customers/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al leer el CSV"); setImportFile(null); }
  };

  const doImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const r = await adminApi.post("/admin/customers/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.inserted} clientes importados (${r.data.skipped} omitidos)`);
      setImportFile(null); setImportPreview(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al importar"); }
    setImporting(false);
  };

  const closeImport = () => { setImportFile(null); setImportPreview(null); if (fileRef.current) fileRef.current.value = ""; };

  const sendActivation = async (c) => {
    if (!c.email) { toast.error("El cliente no tiene email"); return; }
    if (!window.confirm(`¿Enviar email de activación a ${c.email}?`)) return;
    try {
      await adminApi.post(`/admin/customers/${c.id}/send-activation`);
      toast.success(`Activación enviada a ${c.email}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al enviar"); }
  };

  const sendActivationAll = async () => {
    if (!window.confirm("¿Enviar email de activación a TODOS los clientes que aún no tienen contraseña?")) return;
    try {
      const r = await adminApi.post("/admin/customers/send-activation-all");
      toast.success(`${r.data.sent} emails de activación enviados`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al enviar"); }
  };


  const save = async () => {
    try {
      if (editing.id) await adminApi.put(`/admin/customers/${editing.id}`, editing);
      else await adminApi.post("/admin/customers", editing);
      toast.success("Cliente guardado");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const del = async (id) => {
    if (!window.confirm("¿Eliminar cliente?")) return;
    await adminApi.delete(`/admin/customers/${id}`);
    toast.success("Eliminado");
    load();
  };
  const view = async (id) => {
    const r = await adminApi.get(`/admin/customers/${id}`);
    setViewing(r.data);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-3xl tracking-tighter">Clientes</h1>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={onPickFile} className="hidden" data-testid="import-csv-input" />
          <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 border border-brand text-brand rounded-sm px-3 py-2 text-sm hover:bg-brand hover:text-white transition-colors" data-testid="import-csv-btn"><Upload className="h-4 w-4" /> Importar CSV</button>
          <button onClick={sendActivationAll} className="inline-flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm hover:border-brand" data-testid="send-activation-all-btn"><Send className="h-4 w-4" /> Activación a todos</button>
          <button onClick={() => downloadBlob("/admin/export/customers", "clientes.csv")} className="inline-flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm hover:border-brand" data-testid="export-csv"><Download className="h-4 w-4" /> CSV</button>
          <button onClick={() => downloadBlob("/admin/export-xlsx/customers", "clientes.xlsx")} className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-sm px-3 py-2 text-sm hover:bg-emerald-700" data-testid="export-xlsx"><Download className="h-4 w-4" /> Excel</button>
          <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-brand text-white rounded-sm px-4 py-2 text-sm font-semibold hover:bg-brand-dark" data-testid="new-customer-btn"><Plus className="h-4 w-4" /> Nuevo</button>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar nombre, empresa, email, CIF..." className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-sm bg-white" data-testid="customer-search" />
      </form>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-ink text-white"><tr><th className="text-left px-3 py-2">Nombre</th><th className="text-left px-3 py-2">Empresa</th><th className="text-left px-3 py-2">Email</th><th className="text-left px-3 py-2">Teléfono</th><th className="text-right px-3 py-2"></th></tr></thead>
          <tbody>
            {customers.length === 0 ? <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">Sin clientes.</td></tr> :
             customers.map((c, i) => (
              <tr key={c.id} className={i % 2 ? "bg-secondary/40" : "bg-white"} data-testid={`customer-row-${c.id}`}>
                <td className="px-3 py-2">{c.name}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.company}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.email}</td>
                <td className="px-3 py-2 text-muted-foreground">{c.phone}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => sendActivation(c)} title="Enviar activación" className="p-1.5 hover:text-brand" data-testid={`send-activation-${c.id}`}><Mail className="h-4 w-4" /></button>
                  <button onClick={() => view(c.id)} className="p-1.5 hover:text-brand"><Eye className="h-4 w-4" /></button>
                  <button onClick={() => setEditing(c)} className="p-1.5 hover:text-brand"><Edit className="h-4 w-4" /></button>
                  <button onClick={() => del(c.id)} className="p-1.5 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-sm w-full max-w-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="font-heading font-semibold text-lg">{editing.id ? "Editar" : "Nuevo"} cliente</h2><button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button></div>
            {Object.keys(EMPTY).map((k) => (
              <input key={k} placeholder={k} value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} className="w-full px-3 py-2 text-sm border border-border rounded-sm" data-testid={`customer-${k}`} />
            ))}
            <button onClick={save} className="w-full bg-brand text-white py-2.5 rounded-sm font-semibold hover:bg-brand-dark" data-testid="save-customer-btn">Guardar</button>
          </div>
        </div>
      )}

      {importFile && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={closeImport}>
          <div className="bg-white rounded-sm w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="import-modal">
            <div className="flex items-center justify-between"><h2 className="font-heading font-semibold text-lg">Importar clientes (CSV)</h2><button onClick={closeImport}><X className="h-5 w-5" /></button></div>
            <p className="text-sm text-muted-foreground truncate">{importFile.name}</p>
            {!importPreview ? <p className="text-sm py-6 text-center text-muted-foreground">Analizando archivo…</p> : (
              <>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="border border-border rounded-sm p-3"><p className="text-muted-foreground text-xs">Filas leídas</p><p className="font-heading font-bold text-xl">{importPreview.total_rows}</p></div>
                  <div className="border border-border rounded-sm p-3 border-brand"><p className="text-muted-foreground text-xs">A importar</p><p className="font-heading font-bold text-xl text-brand" data-testid="import-count">{importPreview.to_import}</p></div>
                  <div className="border border-border rounded-sm p-3"><p className="text-muted-foreground text-xs">Duplicados en archivo</p><p className="font-heading font-bold text-xl">{importPreview.duplicates_in_file}</p></div>
                  <div className="border border-border rounded-sm p-3"><p className="text-muted-foreground text-xs">Ya existen</p><p className="font-heading font-bold text-xl">{importPreview.already_exist}</p></div>
                </div>
                {importPreview.sample?.length > 0 && (
                  <div className="border border-border rounded-sm overflow-hidden">
                    <table className="w-full text-xs"><thead className="bg-secondary/60"><tr><th className="text-left px-2 py-1.5">Nombre</th><th className="text-left px-2 py-1.5">Email</th><th className="text-right px-2 py-1.5">Ventas</th></tr></thead>
                      <tbody>{importPreview.sample.map((s, i) => <tr key={i} className="border-t border-border"><td className="px-2 py-1.5">{s.name}</td><td className="px-2 py-1.5 text-muted-foreground">{s.email}</td><td className="px-2 py-1.5 text-right">{eur(s.total_sales)}</td></tr>)}</tbody>
                    </table>
                  </div>
                )}
                <button onClick={doImport} disabled={importing || importPreview.to_import === 0} className="w-full bg-brand text-white py-2.5 rounded-sm font-semibold hover:bg-brand-dark disabled:opacity-50" data-testid="confirm-import-btn">{importing ? "Importando…" : `Importar ${importPreview.to_import} clientes`}</button>
              </>
            )}
          </div>
        </div>
      )}

      {viewing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setViewing(null)}>
          <div className="bg-white rounded-sm w-full max-w-lg p-6 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><h2 className="font-heading font-semibold text-lg">{viewing.name}</h2><button onClick={() => setViewing(null)}><X className="h-5 w-5" /></button></div>
            <p className="text-sm text-muted-foreground">{viewing.company} · {viewing.cif}</p>
            <p className="text-sm text-muted-foreground">{viewing.email} · {viewing.phone}</p>
            {(viewing.total_sales > 0 || viewing.newsletter) && (
              <p className="text-sm">{viewing.total_sales > 0 && <span className="mr-3">Ventas históricas: <strong>{eur(viewing.total_sales)}</strong></span>}{viewing.newsletter && <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-xs">Newsletter</span>}</p>
            )}
            <div><p className="font-medium text-sm mt-3 mb-1">Pedidos ({viewing.orders?.length || 0})</p>{(viewing.orders || []).map((o) => <div key={o.id} className="flex justify-between text-sm py-1"><span className="font-mono">{o.number}</span><span>{eur(o.total)}</span></div>)}</div>
            <div><p className="font-medium text-sm mt-3 mb-1">Facturas ({viewing.invoices?.length || 0})</p>{(viewing.invoices || []).map((iv) => <div key={iv.id} className="flex justify-between text-sm py-1"><span className="font-mono">{iv.number}</span><span>{eur(iv.total)}</span></div>)}</div>
          </div>
        </div>
      )}
    </div>
  );
}
